import React, { useState, useEffect } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { useParams } from 'react-router-dom';
import ViewHeader from '../components/ViewHeader';
import KpiCard from '../components/KpiCard';
import DateFilter from '../components/DateFilter';
import EmptyState from '../components/EmptyState';
import api from '../../../../services/api';

const VET_COLORS = ['#0f4c5c', '#f97316', '#10b981', '#ef4444', '#8b5cf6', '#f59e0b'];

const VeterinarianWorkload = ({ onBack }) => {
    const { clinicId } = useParams();
    const [loading, setLoading] = useState(true);
    const [filters, setFilters] = useState({ date: '' });
    const [data, setData] = useState({
        kpis: [],
        workloadData: [],
        vetTable: [],
        upcomingSurgeries: [],
        vetNames: []
    });

    useEffect(() => {
        const fetchWorkloadData = async () => {
            try {
                setLoading(true);
                const params = new URLSearchParams();
                if (filters.date) params.append('date', filters.date);
                const response = await api.get(`/api/v1/clinics/${clinicId}/dashboard/vet-workload?${params.toString()}`);
                if (response.data?.status === 'success') {
                    setData(response.data.data);
                }
            } catch (error) {
                console.error("Error fetching vet workload data:", error);
            } finally {
                setLoading(false);
            }
        };

        if (clinicId) {
            fetchWorkloadData();
        }
    }, [clinicId, filters]);

    if (loading) {
        return <div className="p-4">Loading veterinarian workload report...</div>;
    }

    const hasVetTable = data.vetTable.length > 0;
    const hasSurgeries = data.upcomingSurgeries.length > 0;

    const subtitle = filters.date
        ? new Date(filters.date).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })
        : 'Today';

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', flex: 1, minHeight: 0 }}>
            <ViewHeader
                title="Veterinarian Workload"
                subtitle={subtitle}
                onBack={onBack}
                filter={
                    <DateFilter
                        mode="date"
                        value={filters}
                        onChange={setFilters}
                    />
                }
            />

            {/* Top row */}
            <div style={{ display: 'flex', gap: '16px' }}>
                {/* Key metrics */}
                <div className="clinic-dashboard-section-card" style={{ flex: 1, animationDelay: '0ms' }}>
                    <div className="clinic-dashboard-section-title">Key metrics</div>
                    <div className="clinic-dashboard-kpi-row">
                        {data.kpis.map((kpi, i) => <KpiCard key={i} {...kpi} />)}
                    </div>
                </div>

                {/* Workload over time */}
                <div className="clinic-dashboard-section-card" style={{ flex: 1, display: 'flex', flexDirection: 'column', animationDelay: '80ms' }}>
                    <div className="clinic-dashboard-section-title">Workload over time</div>
                    <div className="clinic-dashboard-chart-wrap" style={{ minHeight: '200px' }}>
                        <ResponsiveContainer width="100%" height="100%">
                            <LineChart data={data.workloadData} margin={{ top: 5, right: 20, left: -10, bottom: 5 }}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                                <XAxis
                                    dataKey="time"
                                    axisLine={false}
                                    tickLine={false}
                                    tick={{ fill: '#9ca3af', fontSize: 10 }}
                                />
                                <YAxis
                                    axisLine={false}
                                    tickLine={false}
                                    tick={{ fill: '#9ca3af', fontSize: 12 }}
                                />
                                <Tooltip
                                    contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}
                                />
                                {data.vetNames.map((name, index) => (
                                    <Line
                                        key={name}
                                        animationBegin={600}
                                        type="monotone"
                                        dataKey={name}
                                        stroke={VET_COLORS[index % VET_COLORS.length]}
                                        strokeWidth={2}
                                        dot={{ r: 3 }}
                                    />
                                ))}
                            </LineChart>
                        </ResponsiveContainer>
                    </div>
                </div>
            </div>

            {/* Bottom row */}
            <div style={{ display: 'flex', gap: '16px', flex: 1, minHeight: 0 }}>
                {/* Vet workload table */}
                <div className="clinic-dashboard-section-card" style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', animationDelay: '160ms' }}>
                    <div className="clinic-dashboard-section-title">Vet Workload</div>
                    {hasVetTable ? (
                        <div style={{ overflow: 'auto', flex: 1 }}>
                            <table className="clinic-dashboard-table">
                                <thead>
                                    <tr>
                                        <th>vet</th>
                                        <th>Total Apt</th>
                                        <th>consulting</th>
                                        <th>inpatient</th>
                                        <th>Presedure</th>
                                        <th>Status</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {data.vetTable.map((row, i) => (
                                        <tr key={i}>
                                            <td>{row.vet}</td>
                                            <td>{row.total}</td>
                                            <td>{row.consulting}</td>
                                            <td>{row.inpatient}</td>
                                            <td>{row.procedure}</td>
                                            <td>
                                                <span className={`clinic-dashboard-status-badge ${row.status.toLowerCase()}`}>
                                                    {row.status}
                                                </span>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    ) : (
                        <EmptyState message="No vet workload data for this day" />
                    )}
                </div>

                {/* Upcoming surgeries */}
                <div className="clinic-dashboard-section-card" style={{ flex: 0.7, display: 'flex', flexDirection: 'column', animationDelay: '240ms' }}>
                    <div className="clinic-dashboard-section-title">Upcoming surgeries</div>
                    {hasSurgeries ? (
                        <div className="clinic-dashboard-surgery-list" style={{ flex: 1, justifyContent: 'center' }}>
                            {data.upcomingSurgeries.map((surgery, i) => (
                                <div key={i} className="clinic-dashboard-surgery-item">
                                    <div className="clinic-dashboard-surgery-info">
                                        <span className="clinic-dashboard-surgery-pet">{surgery.pet}</span>
                                        <span className="clinic-dashboard-surgery-procedure">{surgery.procedure}</span>
                                    </div>
                                    <span className="clinic-dashboard-surgery-date">{surgery.date}</span>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <EmptyState message="No upcoming surgeries" />
                    )}
                </div>
            </div>
        </div>
    );
};

export default VeterinarianWorkload;
